// Workshop store using Zustand for KATA Dashboard

import { create } from 'zustand';
import { Workshop, WorkshopDetail, ApiResponse } from '../types/api';
import { WorkshopService, WorkshopFilters, WorkshopCreateData, WorkshopUpdateData } from '../services/workshop';

interface WorkshopState {
  // Data
  workshops: Workshop[];
  currentWorkshop: WorkshopDetail | null;
  totalCount: number;
  
  // UI State
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  error: string | null;
  
  // Filters
  filters: WorkshopFilters;
  
  // Pagination
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface WorkshopActions {
  // Data fetching
  fetchWorkshops: (filters?: WorkshopFilters) => Promise<void>;
  fetchWorkshop: (id: number) => Promise<void>;
  refreshWorkshops: () => Promise<void>;
  
  // CRUD operations
  createWorkshop: (data: WorkshopCreateData) => Promise<WorkshopDetail>;
  updateWorkshop: (id: number, data: WorkshopUpdateData) => Promise<WorkshopDetail>;
  deleteWorkshop: (id: number) => Promise<void>;
  updateProgress: (id: number, progress: number, currentCondition?: number) => Promise<WorkshopDetail>;
  
  // Filters and pagination
  setFilters: (filters: Partial<WorkshopFilters>) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  
  // UI state
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearCurrentWorkshop: () => void;
}

type WorkshopStore = WorkshopState & WorkshopActions;

const STORAGE_KEY_FROM = 'kata_deadline_from';
const STORAGE_KEY_TO = 'kata_deadline_to';
const EVENT_NAME = 'kata_date_filter_change';
const STORAGE_KEY_ALV_ID = 'kata_plant_filter';
const PLANT_EVENT_NAME = 'kata_plant_filter_change';

const initialFilters: WorkshopFilters = {
  page: 1,
  page_size: 20,
  ordering: '-created_at',
  deadline_from: localStorage.getItem(STORAGE_KEY_FROM) || undefined,
  deadline_to: localStorage.getItem(STORAGE_KEY_TO) || undefined,
  alv_id: localStorage.getItem(STORAGE_KEY_ALV_ID) || undefined
};

export const useWorkshopStore = create<WorkshopStore>((set, get) => {
  // Listen for custom events to sync across stores
  if (typeof window !== 'undefined') {
    window.addEventListener(EVENT_NAME, (e: any) => {
      const { startDate, endDate } = e.detail;
      const { filters } = get();
      
      // Only update if values are different
      if (startDate !== filters.deadline_from || endDate !== filters.deadline_to) {
        const updatedFilters = { 
          ...filters, 
          deadline_from: startDate, 
          deadline_to: endDate,
          page: 1 
        };
        set({ filters: updatedFilters, currentPage: 1 });
        // Automatically fetch new data when sync occurs
        get().fetchWorkshops(updatedFilters);
      }
    });

    window.addEventListener(PLANT_EVENT_NAME, (e: any) => {
      const { alvId } = e.detail;
      const { filters } = get();
      
      if (alvId !== filters.alv_id) {
        const newFilters = { ...filters, alv_id: alvId, page: 1 };
        set({ filters: newFilters, currentPage: 1 });
        get().fetchWorkshops(newFilters);
      }
    });
  }

  return {
  // Initial state
  workshops: [],
  currentWorkshop: null,
  totalCount: 0,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  filters: initialFilters,
  currentPage: 1,
  pageSize: 20,
  hasNext: false,
  hasPrevious: false,

  // Set loading state
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Set error message
  setError: (error: string | null) => {
    set({ error });
  },

  // Clear error message
  clearError: () => {
    set({ error: null });
  },

  // Clear current workshop
  clearCurrentWorkshop: () => {
    set({ currentWorkshop: null });
  },

  // Set filters
  setFilters: (newFilters: Partial<WorkshopFilters>) => {
    const { filters } = get();
    
    // Check if dates are being updated
    let dateChanged = false;
    let newStartDate = filters.deadline_from;
    let newEndDate = filters.deadline_to;

    // Update local storage if deadlines are changed
    if ('deadline_from' in newFilters) {
      dateChanged = true;
      newStartDate = newFilters.deadline_from;
      if (newFilters.deadline_from) {
        localStorage.setItem(STORAGE_KEY_FROM, newFilters.deadline_from);
      } else {
        localStorage.removeItem(STORAGE_KEY_FROM);
      }
    }
    
    if ('deadline_to' in newFilters) {
      dateChanged = true;
      newEndDate = newFilters.deadline_to;
      if (newFilters.deadline_to) {
        localStorage.setItem(STORAGE_KEY_TO, newFilters.deadline_to);
      } else {
        localStorage.removeItem(STORAGE_KEY_TO);
      }
    }

    // Dispatch event if dates changed
    if (dateChanged) {
      const event = new CustomEvent(EVENT_NAME, { 
        detail: { startDate: newStartDate, endDate: newEndDate } 
      });
      window.dispatchEvent(event);
    }

    const updatedFilters = { ...filters, ...newFilters, page: 1 }; // Reset to page 1 when filtering
    set({ filters: updatedFilters, currentPage: 1 });
  },

  // Clear all filters
  clearFilters: () => {
    localStorage.removeItem(STORAGE_KEY_FROM);
    localStorage.removeItem(STORAGE_KEY_TO);
    
    // Dispatch clear event
    const event = new CustomEvent(EVENT_NAME, { 
      detail: { startDate: undefined, endDate: undefined } 
    });
    window.dispatchEvent(event);

    set({ filters: { ...initialFilters, deadline_from: undefined, deadline_to: undefined }, currentPage: 1 });
  },

  // Set current page
  setPage: (page: number) => {
    const { filters } = get();
    set({ 
      filters: { ...filters, page },
      currentPage: page 
    });
  },

  // Set page size
  setPageSize: (pageSize: number) => {
    const { filters } = get();
    set({ 
      filters: { ...filters, page_size: pageSize, page: 1 },
      pageSize,
      currentPage: 1 
    });
  },

  // Fetch workshops with current filters
  fetchWorkshops: async (customFilters?: WorkshopFilters) => {
    const { filters, setLoading, setError, clearError } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const filtersToUse = customFilters || filters;
      const response = await WorkshopService.getWorkshops(filtersToUse);
      
      set({
        workshops: response.results,
        totalCount: response.count,
        hasNext: !!response.next,
        hasPrevious: !!response.previous,
        currentPage: filtersToUse.page || 1
      });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch workshops');
    } finally {
      setLoading(false);
    }
  },

  // Refresh workshops with current filters
  refreshWorkshops: async () => {
    const { fetchWorkshops, filters } = get();
    await fetchWorkshops(filters);
  },

  // Fetch single workshop
  fetchWorkshop: async (id: number) => {
    const { setLoading, setError, clearError } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const workshop = await WorkshopService.getWorkshop(id);
      set({ currentWorkshop: workshop });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch workshop');
    } finally {
      setLoading(false);
    }
  },

  // Create new workshop
  createWorkshop: async (data: WorkshopCreateData) => {
    const { setError, clearError, refreshWorkshops } = get();
    
    try {
      set({ isCreating: true });
      clearError();
      
      const workshop = await WorkshopService.createWorkshop(data);
      
      // Refresh the list to include the new workshop
      await refreshWorkshops();
      
      return workshop;
      
    } catch (error: any) {
      setError(error.message || 'Failed to create workshop');
      throw error;
    } finally {
      set({ isCreating: false });
    }
  },

  // Update workshop
  updateWorkshop: async (id: number, data: WorkshopUpdateData) => {
    const { setError, clearError, refreshWorkshops, currentWorkshop } = get();
    
    try {
      set({ isUpdating: true });
      clearError();
      
      const updatedWorkshop = await WorkshopService.updateWorkshop(id, data);
      
      // Update current workshop if it's the one being updated
      if (currentWorkshop && currentWorkshop.id === id) {
        set({ currentWorkshop: updatedWorkshop });
      }
      
      // Refresh the list
      await refreshWorkshops();
      
      return updatedWorkshop;
      
    } catch (error: any) {
      setError(error.message || 'Failed to update workshop');
      throw error;
    } finally {
      set({ isUpdating: false });
    }
  },

  // Delete workshop
  deleteWorkshop: async (id: number) => {
    const { setError, clearError, refreshWorkshops, currentWorkshop } = get();
    
    try {
      set({ isLoading: true });
      clearError();
      
      await WorkshopService.deleteWorkshop(id);
      
      // Clear current workshop if it's the one being deleted
      if (currentWorkshop && currentWorkshop.id === id) {
        set({ currentWorkshop: null });
      }
      
      // Refresh the list
      await refreshWorkshops();
      
    } catch (error: any) {
      setError(error.message || 'Failed to delete workshop');
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Update workshop progress
  updateProgress: async (id: number, progress: number, currentCondition?: number) => {
    const { setError, clearError, refreshWorkshops, currentWorkshop } = get();
    
    try {
      set({ isUpdating: true });
      clearError();
      
      const progressData = {
        progress_rate: progress,
        ...(currentCondition !== undefined && { current_condition: currentCondition })
      };
      
      const updatedWorkshop = await WorkshopService.updateProgress(id, progressData);
      
      // Update current workshop if it's the one being updated
      if (currentWorkshop && currentWorkshop.id === id) {
        set({ currentWorkshop: updatedWorkshop });
      }
      
      // Refresh the list
      await refreshWorkshops();
      
      return updatedWorkshop;
      
    } catch (error: any) {
      setError(error.message || 'Failed to update progress');
      throw error;
    } finally {
      set({ isUpdating: false });
    }
  }
  };
});

// Utility hooks for common workshop operations
export const useWorkshops = () => {
  const { workshops, isLoading, error, totalCount, hasNext, hasPrevious } = useWorkshopStore();
  return { workshops, isLoading, error, totalCount, hasNext, hasPrevious };
};

export const useCurrentWorkshop = () => {
  const { currentWorkshop, isLoading, error } = useWorkshopStore();
  return { workshop: currentWorkshop, isLoading, error };
};

export const useWorkshopFilters = () => {
  const { filters, setFilters, clearFilters } = useWorkshopStore();
  return { filters, setFilters, clearFilters };
};
